import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Grid, 
    Header, 
    Button, 
    Message,
    Dimmer,
    Loader,
    Icon,
    Statistic,
    Modal,
    Confirm,
    Label,
    Segment
} from 'semantic-ui-react';
import axiosInstance from '../../lib/auth/axiosApi';
import UploadVideoModal from '../../components/UploadVideoModal';

function DijonVideos() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [playerModalOpen, setPlayerModalOpen] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [videoToDelete, setVideoToDelete] = useState(null);

    useEffect(() => {
        loadVideos();
        loadStats();
    }, []);

    const loadVideos = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/dijon-backend/api/mux/list_assets/?limit=50');
            
            if (response.data.success) {
                setVideos(response.data.assets || []);
                setError(null);
            } else {
                setError(response.data.error || 'Failed to load videos');
            }
        } catch (err) {
            console.error('Error loading videos:', err);
            setError(err.response?.data?.error || 'Failed to load videos');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await axiosInstance.get('/dijon-backend/api/mux/video_stats/');
            
            if (response.data.success) {
                setStats(response.data.stats);
            }
        } catch (err) {
            console.error('Error loading video stats:', err);
        }
    };

    const handleUploadSuccess = (uploadData) => {
        console.log('Upload completed:', uploadData);
        // Refresh the video list after successful upload
        setTimeout(() => {
            loadVideos();
            loadStats();
        }, 3000); // Wait a bit for MUX processing
    };

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        setPlayerModalOpen(true);
    };

    const handleDeleteClick = (video) => {
        setVideoToDelete(video);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!videoToDelete) return;

        try {
            const response = await axiosInstance.delete('/dijon-backend/api/mux/delete_asset/', {
                data: { asset_id: videoToDelete.id }
            });

            if (response.data.success) {
                // Remove video from list
                setVideos(videos.filter(v => v.id !== videoToDelete.id));
                loadStats();
            } else {
                setError(response.data.error || 'Failed to delete video');
            }
        } catch (err) {
            console.error('Error deleting video:', err);
            setError(err.response?.data?.error || 'Failed to delete video');
        } finally {
            setDeleteConfirmOpen(false);
            setVideoToDelete(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'Unknown';
        
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        
        if (minutes === 0) {
            return `${remainingSeconds}s`;
        }
        
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ready': return 'green';
            case 'preparing': return 'yellow';
            case 'errored': return 'red';
            default: return 'grey';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ready': return 'check circle';
            case 'preparing': return 'sync alternate';
            case 'errored': return 'exclamation triangle';
            default: return 'question circle';
        }
    };

    const renderVideoCard = (video) => {
        const playbackId = video.playback_ids?.[0]?.id;
        const thumbnailUrl = playbackId ? `https://image.mux.com/${playbackId}/thumbnail.jpg?width=320&height=180&fit_mode=smartcrop` : null;

        return (
            <Grid.Column key={video.id}>
                <Card style={{ height: '100%' }}>
                    {thumbnailUrl && (
                        <div 
                            style={{ 
                                position: 'relative',
                                cursor: video.status === 'ready' ? 'pointer' : 'default',
                                opacity: video.status === 'ready' ? 1 : 0.6
                            }}
                            onClick={() => video.status === 'ready' && handleVideoClick(video)}
                        >
                            <img 
                                src={thumbnailUrl} 
                                alt="Video thumbnail"
                                style={{ 
                                    width: '100%', 
                                    height: '180px', 
                                    objectFit: 'cover',
                                    display: 'block'
                                }}
                            />
                            {video.status === 'ready' && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(0,0,0,0.7)',
                                    borderRadius: '50%',
                                    padding: '15px',
                                    color: 'white'
                                }}>
                                    <Icon name="play" size="large" />
                                </div>
                            )}
                            <Label 
                                color={getStatusColor(video.status)}
                                size="small"
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px'
                                }}
                            >
                                <Icon name={getStatusIcon(video.status)} />
                                {video.status}
                            </Label>
                        </div>
                    )}
                    
                    <Card.Content>
                        <Card.Header style={{ fontSize: '1em' }}>
                            Video {video.id.slice(-8)}
                        </Card.Header>
                        <Card.Meta>
                            <div>Created: {formatDate(video.created_at)}</div>
                            {video.duration && (
                                <div>Duration: {formatDuration(video.duration)}</div>
                            )}
                            {video.aspect_ratio && (
                                <div>Aspect Ratio: {video.aspect_ratio}</div>
                            )}
                        </Card.Meta>
                    </Card.Content>
                    
                    <Card.Content extra>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button 
                                icon 
                                size="small"
                                onClick={() => handleVideoClick(video)}
                                disabled={video.status !== 'ready'}
                                color="blue"
                            >
                                <Icon name="play" />
                            </Button>
                            <Button 
                                icon 
                                size="small"
                                onClick={() => handleDeleteClick(video)}
                                color="red"
                            >
                                <Icon name="trash" />
                            </Button>
                        </div>
                    </Card.Content>
                </Card>
            </Grid.Column>
        );
    };

    if (loading && videos.length === 0) {
        return (
            <Card fluid>
                <Dimmer active>
                    <Loader>Loading Videos...</Loader>
                </Dimmer>
                <Card.Content style={{ minHeight: '400px' }} />
            </Card>
        );
    }

    return (
        <Card fluid>
            <Card.Content>
                <Card.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Icon name="video" />
                        Video Library
                    </div>
                    <Button 
                        primary 
                        icon="upload" 
                        content="Upload Video"
                        onClick={() => setUploadModalOpen(true)}
                    />
                </Card.Header>
                
                {error && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                {stats && (
                    <Segment>
                        <Grid columns={4} stackable>
                            <Grid.Column textAlign="center">
                                <Statistic size="mini" color="blue">
                                    <Statistic.Value>{stats.total_videos}</Statistic.Value>
                                    <Statistic.Label>Total Videos</Statistic.Label>
                                </Statistic>
                            </Grid.Column>
                            <Grid.Column textAlign="center">
                                <Statistic size="mini" color="green">
                                    <Statistic.Value>{stats.ready_videos}</Statistic.Value>
                                    <Statistic.Label>Ready</Statistic.Label>
                                </Statistic>
                            </Grid.Column>
                            <Grid.Column textAlign="center">
                                <Statistic size="mini" color="yellow">
                                    <Statistic.Value>{stats.processing_videos}</Statistic.Value>
                                    <Statistic.Label>Processing</Statistic.Label>
                                </Statistic>
                            </Grid.Column>
                            <Grid.Column textAlign="center">
                                <Statistic size="mini" color="teal">
                                    <Statistic.Value>{stats.total_duration_formatted || '0 sec'}</Statistic.Value>
                                    <Statistic.Label>Total Duration</Statistic.Label>
                                </Statistic>
                            </Grid.Column>
                        </Grid>
                    </Segment>
                )}

                {videos.length === 0 ? (
                    <Segment placeholder>
                        <Header icon>
                            <Icon name="video" />
                            No videos uploaded yet
                        </Header>
                        <Button 
                            primary 
                            onClick={() => setUploadModalOpen(true)}
                        >
                            Upload Your First Video
                        </Button>
                    </Segment>
                ) : (
                    <Grid columns={3} stackable>
                        {videos.map(renderVideoCard)}
                    </Grid>
                )}
            </Card.Content>

            {/* Video Player Modal */}
            <Modal
                open={playerModalOpen}
                onClose={() => setPlayerModalOpen(false)}
                size="large"
                closeIcon
            >
                <Header>
                    <Icon name="video" />
                    Video Player
                </Header>
                <Modal.Content>
                    {selectedVideo && selectedVideo.playback_ids?.[0] && (
                        <div style={{ width: '100%', aspectRatio: '16/9' }}>
                            <video
                                controls
                                style={{ width: '100%', height: '100%' }}
                                src={`https://stream.mux.com/${selectedVideo.playback_ids[0].id}.m3u8`}
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    )}
                </Modal.Content>
            </Modal>

            {/* Upload Modal */}
            <UploadVideoModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onUploadSuccess={handleUploadSuccess}
            />

            {/* Delete Confirmation */}
            <Confirm
                open={deleteConfirmOpen}
                onCancel={() => setDeleteConfirmOpen(false)}
                onConfirm={confirmDelete}
                header="Delete Video"
                content={`Are you sure you want to delete this video? This action cannot be undone.`}
                confirmButton="Delete"
                cancelButton="Cancel"
            />
        </Card>
    );
}

export default DijonVideos;